import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isApiError } from '@aiptyg/types';
import * as rewardsService from '../services/rewards.service';
import type { RewardDTO } from '../types/rewards.types';

const ERROR_MAP: Record<string, string> = {
  admin_role_required: '无管理员权限',
  network_error: '网络连接失败，请检查网络',
  'invalid earnings range': '无效的时间范围',
};

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    return ERROR_MAP[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

export function useRewardList(enabled = true) {
  const [searchParams, setSearchParams] = useSearchParams();
  const source = searchParams.get('source') || '';
  const agentId = searchParams.get('agent_id') || '';
  const userId = searchParams.get('userId') || '';
  const range = searchParams.get('range') || '';
  const page = parseInt(searchParams.get('page') || '1', 10) || 1;

  const [items, setItems] = useState<RewardDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const updateParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value !== null && value !== '') next.set(key, value);
        else next.delete(key);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const load = useCallback(async (p: number, uid: string, rg: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await rewardsService.listRewards({
        page: p, source, agentId,
        pageSize,
        userId: uid,
        range: rg,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, [pageSize, source, agentId]);

  useEffect(() => {
    if (!enabled) return;
    load(page, userId, range);
  }, [enabled, load, page, userId, range]);

  const setUserId = useCallback((v: string) => {
    updateParams({ userId: v || null, page: null });
  }, [updateParams]);

  const setRange = useCallback((v: string) => {
    updateParams({ range: v || null, page: null });
  }, [updateParams]);

  const setPage = useCallback((value: number) => {
    updateParams({ page: value > 1 ? String(value) : null });
  }, [updateParams]);

  const resetFilters = useCallback(() => {
    updateParams({ userId: null, range: null, page: null });
  }, [updateParams]);

  const reload = useCallback(() => {
    load(page, userId, range);
  }, [load, page, userId, range]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return {
    items, total, page, pageSize, totalPages,
    userId, range,
    loading, error,
    setPage, setUserId, setRange,
    resetFilters, reload,
  };
}
